import { useState } from 'react';
import { useModalContext } from 'utils/contexts/modal/context';
import { Table } from 'ui/table/Table';
import { PaginationFooter } from 'ui/paginationFooter';
import { TableHeader } from 'ui/tableHeader';
import { TableRows } from 'ui/tableRows';
import { FlexSpacer } from 'ui/flexSpacer';
import { configureTemplateHistoryTableColumns, getTemplateDiffUrl } from './utils';
import type {
  TemplateHistoryTableProps,
  TemplateHistoryMetadata,
  TemplateHistoryModalState,
} from '../../types';
import { BulkActionsBar } from 'ui/bulkActionsBar';
import { useNavigate } from 'react-router-dom-v5-compat';

export const TemplateHistoryTable = ({
  template,
  templateContentIds,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  numItems,
  rows,
  setSortedBy,
  sortDirection,
  sortedBy,
}: TemplateHistoryTableProps) => {
  const navigate = useNavigate();
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<
    Array<TemplateHistoryMetadata['id']>
  >([]);

  const { showModal } = useModalContext<TemplateHistoryModalState>();

  const isSelectDisabled = selectedTemplateIds.length >= 2;
  const columns = configureTemplateHistoryTableColumns(
    templateContentIds,
    template.id,
    showModal,
    template.whatsAppContentId,
  );

  return (
    <>
      {!!selectedTemplateIds.length && (
        <BulkActionsBar
          actions={[
            {
              active: selectedTemplateIds.length > 0,
              label: 'Compare',
              onClick: () => {
                const comparisonUrl = getTemplateDiffUrl(
                  template.id,
                  selectedTemplateIds[0],
                  selectedTemplateIds[1],
                );
                navigate(comparisonUrl);
              },
              sortIndex: 1,
              isDisabled: selectedTemplateIds.length < 2,
            },
          ]}
          numSelected={selectedTemplateIds.length}
        />
      )}
      <Table data-test="template-history-table">
        <TableHeader
          columns={columns}
          hasCheckboxColumn
          setSortedBy={setSortedBy}
          sortDirection={sortDirection}
          sortedBy={sortedBy}
        />
        <TableRows
          columns={columns}
          hasCheckboxColumn
          maxSelectedRows={2}
          rows={rows}
          selectedRows={selectedTemplateIds}
          onSelectRow={(row) => {
            const { id } = row;
            if (selectedTemplateIds.includes(id)) {
              setSelectedTemplateIds(selectedTemplateIds.filter((templateId) => templateId !== id));
            } else if (!isSelectDisabled) {
              setSelectedTemplateIds([...selectedTemplateIds, id]);
            }
          }}
        />
      </Table>
      {rows?.length === 0 && (
        <FlexSpacer
          data-test="template-history-table-empty-container"
          padding={'cardPadding'}
          justifyContent="center"
        >
          No results found.
        </FlexSpacer>
      )}
      <PaginationFooter
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        numItems={numItems}
        onUpdate={({ page, pageSize }) => {
          setCurrentPage(page);
          setItemsPerPage(pageSize);
        }}
      />
    </>
  );
};
